import { useEffect, useMemo, useState } from 'react';
import {
  RiArrowRightLine,
  RiCheckDoubleLine,
  RiFlashlightLine,
  RiLightbulbLine,
  RiRefreshLine,
  RiShieldCheckLine,
  RiSubtractLine,
} from 'react-icons/ri';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { useStore } from '../state/store';
import { api } from '../state/api';
import {
  ACH_RATINGS,
  ACH_RATING_META,
  analyzeAch,
  emptyMatrix,
  type AchRating,
} from '../analysis/ach';
import type { AchMatrix } from '../state/domain';
import { cn } from '@/lib/utils';

const CELL_STYLE: Record<AchRating, string> = {
  '++': 'bg-emerald-600 text-white',
  '+': 'bg-emerald-100 text-emerald-800',
  NA: 'bg-muted text-muted-foreground',
  '-': 'bg-amber-100 text-amber-800',
  '--': 'bg-[var(--color-cord)] text-white',
};

const VERDICT: Record<string, { label: string; variant: 'evidence' | 'assess' | 'cord' }> = {
  favored: { label: '相对最可信', variant: 'evidence' },
  possible: { label: '仍有可能', variant: 'assess' },
  unlikely: { label: '否证较多', variant: 'cord' },
};

export function AchPanel() {
  const engagementId = useStore(state => state.activeEngagementId);
  const toast = useStore(state => state.toast);

  const [matrix, setMatrix] = useState<AchMatrix | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    if (!engagementId) return;
    setLoading(true);
    try {
      setMatrix(await api.getAch(engagementId));
    } catch (error) {
      toast('error', (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engagementId]);

  const analysis = useMemo(
    () => (matrix ? analyzeAch(matrix.cells, matrix.hypotheses, matrix.evidence) : null),
    [matrix]
  );

  const rankOf = (hypothesisId: string) =>
    analysis?.hypothesisScores.find(score => score.hypothesisId === hypothesisId)?.rank ?? 0;
  const cellOf = (hypothesisId: string, evidenceId: string) =>
    matrix?.cells.find(cell => cell.hypothesisId === hypothesisId && cell.evidenceId === evidenceId);

  const cycle = async (hypothesisId: string, evidenceId: string) => {
    if (!engagementId) return;
    const current = cellOf(hypothesisId, evidenceId)?.rating;
    const index = current ? ACH_RATINGS.indexOf(current) : -1;
    const next = ACH_RATINGS[(index + 1) % ACH_RATINGS.length];
    // 乐观更新
    setMatrix(prev =>
      prev
        ? {
            ...prev,
            cells: [
              ...prev.cells.filter(c => !(c.hypothesisId === hypothesisId && c.evidenceId === evidenceId)),
              {
                id: `${hypothesisId}::${evidenceId}`,
                hypothesisId,
                evidenceId,
                rating: next,
                note: null,
                updatedAt: new Date().toISOString(),
              },
            ],
          }
        : prev
    );
    try {
      await api.rateAch({ engagementId, hypothesisId, evidenceId, rating: next });
    } catch (error) {
      toast('error', (error as Error).message);
      await refresh();
    }
  };

  const autofill = async () => {
    if (!engagementId) return;
    try {
      const { written } = await api.autofillAch(engagementId);
      toast(written ? 'success' : 'info', written ? `已预填 ${written} 个评级` : '没有可预填的支持 / 反证关系');
      await refresh();
    } catch (error) {
      toast('error', (error as Error).message);
    }
  };

  if (!matrix || matrix.hypotheses.length === 0 || matrix.evidence.length === 0) {
    return (
      <div className="flex flex-col gap-3 p-3">
        <span className="label-caps">ACH 竞争性假设矩阵</span>
        <div className="rounded-[var(--radius)] border border-dashed border-border p-5 text-center text-[11.5px] leading-relaxed text-muted-foreground">
          需要至少 <b>1 条研判</b> 与 <b>1 条证据</b>。
          <br />
          先在「研判」登记假设，在「检索 / 证据」归档证据，然后在此逐格评级。
        </div>
      </div>
    );
  }

  const evidenceRows = matrix.evidence.slice(0, 24);
  const missing = emptyMatrix(
    matrix.cells,
    matrix.hypotheses.map(h => h.id),
    evidenceRows.map(e => e.id)
  );
  const filled = evidenceRows.length * matrix.hypotheses.length - missing;

  return (
    <div className="flex flex-col gap-3 p-3">
      <div className="flex items-center gap-1.5">
        <RiLightbulbLine className="size-3.5 text-[var(--color-lead)]" />
        <span className="text-[12px] font-semibold">ACH 竞争性假设矩阵</span>
        <Badge variant="muted" className="ml-auto">
          {filled}/{evidenceRows.length * matrix.hypotheses.length}
        </Badge>
      </div>

      <p className="rounded-[var(--radius-sm)] border border-border bg-muted/40 p-2 text-[10.5px] leading-relaxed text-muted-foreground">
        原则：<b>不看谁被支持得多，只看谁被否证得少</b>。点击单元格循环切换评级；
        「诊断价值」为 0 的证据对判断无帮助。
      </p>

      <div className="flex flex-wrap gap-1.5">
        <Button size="xs" variant="outline" onClick={autofill}>
          <RiFlashlightLine className="size-3" />
          从研判预填
        </Button>
        <Button size="xs" variant="ghost" onClick={refresh} disabled={loading}>
          <RiRefreshLine className={cn('size-3', loading && 'animate-spin')} />
          刷新
        </Button>
      </div>

      {/* 矩阵 */}
      <div className="overflow-x-auto rounded-[var(--radius)] border border-border">
        <table className="w-full border-collapse text-[10.5px]">
          <thead>
            <tr className="bg-muted/50">
              <th className="sticky left-0 z-[1] min-w-[150px] bg-muted/80 px-2 py-1.5 text-left font-medium">
                证据 \ 假设
              </th>
              {matrix.hypotheses.map((hypothesis, index) => (
                <th
                  key={hypothesis.id}
                  className="min-w-[62px] border-l border-border px-1 py-1.5 text-center font-medium"
                  title={hypothesis.statement}
                >
                  <div className="font-mono text-[10px]">H{index + 1}</div>
                  <div className="mx-auto mt-0.5 w-fit rounded bg-card px-1 text-[9px] text-muted-foreground">
                    #{rankOf(hypothesis.id)}
                  </div>
                </th>
              ))}
              <th className="border-l border-border px-1.5 py-1.5 text-center font-medium">诊断价值</th>
            </tr>
          </thead>
          <tbody>
            {evidenceRows.map((evidence, rowIndex) => {
              const score = analysis?.evidenceScores.find(s => s.evidenceId === evidence.id);
              const diagnostic = score?.diagnosticValue ?? 0;
              return (
                <tr key={evidence.id} className={rowIndex % 2 ? 'bg-muted/20' : ''}>
                  <td className="sticky left-0 z-[1] max-w-[220px] bg-card px-2 py-1">
                    <div className="flex items-center gap-1">
                      <Badge variant="outline" className="shrink-0 font-mono">
                        {evidence.sourceReliability ?? 'C'}
                        {evidence.infoCredibility ?? 3}
                      </Badge>
                      <span className="truncate" title={evidence.title ?? evidence.kind}>
                        {evidence.title ?? evidence.kind}
                      </span>
                    </div>
                  </td>
                  {matrix.hypotheses.map(hypothesis => {
                    const cell = cellOf(hypothesis.id, evidence.id);
                    const rating = cell?.rating;
                    return (
                      <td key={hypothesis.id} className="border-l border-border p-0.5 text-center">
                        <button
                          type="button"
                          onClick={() => cycle(hypothesis.id, evidence.id)}
                          title={rating ? ACH_RATING_META[rating].label : '未评级（点击开始）'}
                          className={cn(
                            'flex size-6 items-center justify-center rounded-[3px] font-mono text-[10px] transition-colors',
                            rating ? CELL_STYLE[rating] : 'border border-dashed border-border text-muted-foreground'
                          )}
                        >
                          {rating ? ACH_RATING_META[rating].short : ''}
                        </button>
                      </td>
                    );
                  })}
                  <td className="border-l border-border px-1.5 text-center">
                    {diagnostic >= 2 ? (
                      <Badge variant="lead" className="font-mono">
                        {diagnostic}
                      </Badge>
                    ) : (
                      <span className="font-mono text-muted-foreground">0</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-[10px] text-muted-foreground">
        {ACH_RATINGS.map(rating => (
          <span key={rating} className="inline-flex items-center gap-1">
            <span className={cn('grid size-4 place-items-center rounded-[3px] font-mono', CELL_STYLE[rating])}>
              {ACH_RATING_META[rating].short}
            </span>
            {ACH_RATING_META[rating].label}
          </span>
        ))}
      </div>

      {/* 排序结果 */}
      {analysis && (
        <>
          <div className="flex flex-col gap-1.5">
            <span className="label-caps">否证排序（越靠前越可信）</span>
            {analysis.hypothesisScores.map(score => {
              const hypothesis = matrix.hypotheses.find(h => h.id === score.hypothesisId);
              const verdict = VERDICT[score.verdict];
              const maxScore = Math.max(1, ...analysis.hypothesisScores.map(s => s.disconfirmScore));
              return (
                <div
                  key={score.hypothesisId}
                  className={cn(
                    'rounded-[var(--radius-sm)] border bg-card p-2',
                    score.rank === 1 ? 'border-[var(--color-evidence)]/50' : 'border-border'
                  )}
                >
                  <div className="flex items-center gap-1.5">
                    <span className="font-mono text-[10] text-muted-foreground">#{score.rank}</span>
                    <Badge variant={verdict.variant}>{verdict.label}</Badge>
                    <span className="ml-auto font-mono text-[10px] text-muted-foreground">
                      否证分 {score.disconfirmScore}
                    </span>
                  </div>
                  <p className="mt-1 line-clamp-2 text-[11px] leading-relaxed" title={hypothesis?.statement}>
                    {hypothesis?.statement}
                  </p>
                  <div className="mt-1 h-1 overflow-hidden rounded-full bg-muted">
                    <div
                      className={cn(
                        'h-full rounded-full',
                        score.disconfirmScore === 0 ? 'bg-[var(--color-evidence)]' : 'bg-[var(--color-cord)]/70'
                      )}
                      style={{ width: `${(score.disconfirmScore / maxScore) * 100}%` }}
                    />
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-[10px] text-muted-foreground">
                    <span className="inline-flex items-center gap-0.5 text-[var(--color-evidence)]">
                      <RiCheckDoubleLine className="size-3" />
                      强一致 {score.strongConsistent}
                    </span>
                    <span className="text-emerald-700">一致 {score.consistent}</span>
                    <span className="inline-flex items-center gap-0.5 text-[var(--color-cord)]">
                      <RiSubtractLine className="size-3" />
                      不一致 {score.inconsistent}
                    </span>
                    <span className="inline-flex items-center gap-0.5 text-[var(--color-cord)]">
                      <RiShieldCheckLine className="size-3" />
                      强不一致 {score.strongInconsistent}
                    </span>
                    <span>不适用 {score.notApplicable}</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="label-caps">关键证据（能区分假设）</span>
            {analysis.keyEvidence.length > 0 ? (
              <div className="flex flex-col gap-1">
                {analysis.keyEvidence.slice(0, 8).map(id => {
                  const evidence = matrix.evidence.find(e => e.id === id);
                  const score = analysis.evidenceScores.find(s => s.evidenceId === id);
                  return (
                    <div
                      key={id}
                      className="flex items-center gap-1.5 rounded-[var(--radius-sm)] border border-border bg-card px-2 py-1.5 text-[10.5px]"
                    >
                      <RiArrowRightLine className="size-3 shrink-0 text-[var(--color-lead)]" />
                      <span className="truncate">{evidence?.title ?? evidence?.kind}</span>
                      <Badge variant="lead" className="ml-auto shrink-0">
                        区分度 {score?.diagnosticValue ?? 0}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-[10.5px] text-muted-foreground">
                暂无有区分度的证据 —— 当前证据对所有假设的评级相同，无法帮助判断。
              </p>
            )}
          </div>

          {analysis.nonDiagnosticEvidence.length > 0 && (
            <div className="flex flex-col gap-1.5">
              <span className="label-caps">无诊断价值（{analysis.nonDiagnosticEvidence.length}）</span>
              <p className="text-[10.5px] leading-relaxed text-muted-foreground">
                这些证据对所有假设一致或不适用，不构成判断依据，建议补充更有针对性的取证。
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}